import { type CaseV1Data, type ConditionV1Data, hashAnswer, normalizeAnswer } from "@dd/schema";
import type { GameEvent } from "./events";

export const MAX_ACCUSATION_ATTEMPTS = 3;

export interface AccusationAttempt {
  /** Cuántos campos estaban mal — NUNCA cuáles (feedback D-2) */
  readonly wrongCount: number;
  readonly correct: boolean;
  readonly at: number;
}

export interface AccusationState {
  readonly attemptsLeft: number;
  readonly attempts: readonly AccusationAttempt[];
  readonly solved: boolean;
  /** Agotó los intentos sin resolver (fail state B13) */
  readonly failed: boolean;
}

export interface MilestoneState {
  readonly attempts: readonly { wrongCount: number; correct: boolean; at: number }[];
  readonly solved: boolean;
  readonly solvedOnFirstTry: boolean;
}

export interface CaseNotification {
  /** id de la regla/evento que la emitió — la UI deduplica por esto */
  readonly id: string;
  readonly title: string;
  readonly body: string;
  readonly atTick: number;
}

export interface DerivedState {
  readonly viewedArtifacts: ReadonlySet<string>;
  /** Únicos, en orden de fijado (el orden es el layout natural del corcho) */
  readonly pinnedEvidence: readonly string[];
  readonly notes: readonly { text: string; at: number }[];
  /** Cuerdas del corcho; par no-dirigido, sin duplicados */
  readonly boardLinks: readonly { a: string; b: string }[];
  readonly searches: readonly { app: string; q: string }[];
  readonly accusation: AccusationState;
  // ── F1-T03: reloj y desbloqueos (derivados, jamás persistidos) ──
  /** Reloj del expediente: avanza por acciones significativas (jugabilidad §6) */
  readonly clockTicks: number;
  /** Artefactos visibles: no bloqueados + desbloqueados − removidos */
  readonly visibleArtifacts: ReadonlySet<string>;
  /** Removidos por evento scriptado (la ausencia como pista) */
  readonly removedArtifacts: ReadonlySet<string>;
  /** Notificaciones diegéticas emitidas por reglas/eventos, en orden */
  readonly notifications: readonly CaseNotification[];
  /** Efectos disparados que aún no llegaron (latencia diegética) */
  readonly pendingEffects: readonly { id: string; readyAtTick: number }[];
  // ── F1-T04/T05/T06/T08 ──
  readonly milestones: Readonly<Record<string, MilestoneState>>;
  readonly hintsUsed: readonly { level: number; at: number }[];
  /** Citas fijadas desde artefactos (highlight→corcho, C12) */
  readonly quotes: readonly { artifactId: string; quote: string; at: number }[];
  /** Solicitudes enviadas (verbo Solicitar, F1-T08), en orden */
  readonly requests: readonly { formId: string; at: number }[];
  /** Reglas/eventos cuyo efecto ya se aplicó (para status de solicitudes) */
  readonly appliedRules: ReadonlySet<string>;
}

/**
 * Evalúa respuestas contra hashes salteados (C14/G-23). Devuelve SOLO el conteo
 * de campos incorrectos — jamás cuáles: esa opacidad hace inofensivo el JSON.
 */
function evaluateFields(
  fields: readonly { id: string }[],
  solutionHashes: Record<string, string>,
  salt: string,
  answers: Record<string, string>,
): { wrongCount: number; correct: boolean } {
  let wrongCount = 0;
  for (const field of fields) {
    const expected = solutionHashes[field.id];
    const given = answers[field.id];
    if (expected === undefined || given === undefined || hashAnswer(salt, given) !== expected) {
      wrongCount++;
    }
  }
  return { wrongCount, correct: wrongCount === 0 };
}

export function evaluateAccusation(
  kase: CaseV1Data,
  answers: Record<string, string>,
): { wrongCount: number; correct: boolean } {
  const { fields, solutionHashes, salt } = kase.accusation;
  return evaluateFields(fields, solutionHashes, salt, answers);
}

/** Estado mutable interno del replay — lo que las condiciones pueden mirar */
interface ReplayState {
  viewed: Set<string>;
  pinned: Set<string>;
  searched: Set<string>; // términos normalizados
  requested: Set<string>; // formIds
  milestonesSolved: Set<string>;
  ticks: number;
}

function evalCondition(cond: ConditionV1Data, s: ReplayState): boolean {
  if ("viewed" in cond) return s.viewed.has(cond.viewed);
  if ("pinned" in cond) return s.pinned.has(cond.pinned);
  if ("searched" in cond) return s.searched.has(normalizeAnswer(cond.searched));
  if ("milestone" in cond) return s.milestonesSolved.has(cond.milestone);
  if ("requested" in cond) return s.requested.has(cond.requested);
  if ("clockAtLeast" in cond) return s.ticks >= cond.clockAtLeast;
  if ("allOf" in cond) return cond.allOf.every((c) => evalCondition(c, s));
  return cond.anyOf.some((c) => evalCondition(c, s));
}

/**
 * Deriva el estado completo del log de eventos. Pura: mismos eventos + mismo caso
 * ⇒ mismo estado (replay determinista para saves, telemetría y simulador).
 *
 * El reloj (§6) avanza con acciones significativas: primer vistazo de un
 * artefacto, término nuevo buscado, solicitud enviada. Los desbloqueos y
 * eventos scriptados se evalúan DENTRO del replay: una regla dispara en el
 * tick donde su condición se vuelve cierta y su efecto llega `afterTicks`
 * después — por eso el orden de eventos importa y el estado jamás se guarda.
 */
export function reduce(events: readonly GameEvent[], kase: CaseV1Data): DerivedState {
  const viewed = new Set<string>();
  const pinned: string[] = [];
  const notes: { text: string; at: number }[] = [];
  const links: { a: string; b: string }[] = [];
  const linkKeys = new Set<string>();
  const searches: { app: string; q: string }[] = [];
  const attempts: AccusationAttempt[] = [];
  const hintsUsed: { level: number; at: number }[] = [];
  const quotes: { artifactId: string; quote: string; at: number }[] = [];
  const requests: { formId: string; at: number }[] = [];
  const milestones: Record<
    string,
    {
      attempts: { wrongCount: number; correct: boolean; at: number }[];
      solved: boolean;
      solvedOnFirstTry: boolean;
    }
  > = {};
  for (const m of kase.milestones) {
    milestones[m.id] = { attempts: [], solved: false, solvedOnFirstTry: false };
  }
  let solved = false;

  const s: ReplayState = {
    viewed,
    pinned: new Set(),
    searched: new Set(),
    requested: new Set(),
    milestonesSolved: new Set(),
    ticks: 0,
  };

  // Reglas y eventos scriptados: cada uno dispara UNA vez; efecto aplica al madurar
  const triggers = [
    ...kase.unlocks.map((u) => ({ kind: "unlock" as const, rule: u })),
    ...kase.scriptedEvents.map((ev) => ({ kind: "scripted" as const, rule: ev })),
  ];
  const triggeredAt = new Map<string, number>(); // ruleId → tick de disparo
  const unlocked = new Set<string>();
  const removed = new Set<string>();
  const notifications: CaseNotification[] = [];
  const applied = new Set<string>();

  /** Aplica los efectos maduros (disparados y con latencia cumplida) */
  const settleEffects = () => {
    for (const t of triggers) {
      const at = triggeredAt.get(t.rule.id);
      if (at === undefined || applied.has(t.rule.id)) continue;
      if (s.ticks < at + t.rule.effect.afterTicks) continue;
      applied.add(t.rule.id);
      if (t.kind === "unlock") {
        for (const a of t.rule.effect.unlockArtifacts) unlocked.add(a);
      } else if (t.rule.effect.removeArtifact) {
        removed.add(t.rule.effect.removeArtifact);
      }
      const n = t.rule.effect.notify;
      if (n) notifications.push({ id: t.rule.id, title: n.title, body: n.body, atTick: s.ticks });
    }
  };

  /** Evalúa condiciones no disparadas contra el estado actual */
  const fireTriggers = () => {
    for (const t of triggers) {
      if (!triggeredAt.has(t.rule.id) && evalCondition(t.rule.when, s)) {
        triggeredAt.set(t.rule.id, s.ticks);
      }
    }
    settleEffects();
  };

  const tick = () => {
    s.ticks++;
    // el paso del reloj puede madurar efectos pendientes y habilitar clockAtLeast
    fireTriggers();
  };

  for (const e of events) {
    switch (e.t) {
      case "artifactViewed": {
        const first = !viewed.has(e.id);
        viewed.add(e.id);
        if (first) tick();
        break;
      }
      case "evidencePinned":
        if (!s.pinned.has(e.id)) {
          s.pinned.add(e.id);
          pinned.push(e.id);
        }
        break;
      case "notePinned":
        notes.push({ text: e.text, at: e.at });
        break;
      case "boardLinked": {
        // Par no-dirigido: A1—A3 y A3—A1 son la misma cuerda
        const key = [e.a, e.b].sort().join("|");
        if (!linkKeys.has(key)) {
          linkKeys.add(key);
          links.push({ a: e.a, b: e.b });
        }
        break;
      }
      case "termSearched": {
        searches.push({ app: e.app, q: e.q });
        const norm = normalizeAnswer(e.q);
        const first = !s.searched.has(norm);
        s.searched.add(norm);
        if (first) tick();
        break;
      }
      case "requestSubmitted":
        requests.push({ formId: e.formId, at: e.at });
        s.requested.add(e.formId);
        tick();
        break;
      case "quotePinned":
        // la misma cita dos veces no duplica tarjeta
        if (!quotes.some((q) => q.artifactId === e.artifactId && q.quote === e.quote)) {
          quotes.push({ artifactId: e.artifactId, quote: e.quote, at: e.at });
        }
        break;
      case "milestoneAttempted": {
        const m = kase.milestones.find((x) => x.id === e.milestoneId);
        const state = milestones[e.milestoneId];
        if (!m || !state || state.solved) break;
        const r = evaluateFields(m.fields, m.solutionHashes, kase.accusation.salt, e.answers);
        state.attempts.push({ wrongCount: r.wrongCount, correct: r.correct, at: e.at });
        if (r.correct) {
          state.solved = true;
          state.solvedOnFirstTry = state.attempts.length === 1;
          s.milestonesSolved.add(e.milestoneId);
          tick();
        }
        break;
      }
      case "hintRequested":
        hintsUsed.push({ level: e.level, at: e.at });
        break;
      case "accusationAttempted": {
        // Bloqueo duro: ni tras resolver ni con intentos agotados se evalúa
        if (solved || attempts.length >= MAX_ACCUSATION_ATTEMPTS) break;
        const result = evaluateAccusation(kase, e.answers);
        attempts.push({ wrongCount: result.wrongCount, correct: result.correct, at: e.at });
        if (result.correct) solved = true;
        break;
      }
    }
    // los eventos que no avanzan el reloj igual pueden disparar condiciones (pinned)
    fireTriggers();
  }

  // Visibilidad final: no bloqueados + desbloqueados − removidos
  const visible = new Set<string>();
  for (const a of kase.artifacts) {
    if (!a.initiallyLocked || unlocked.has(a.id)) visible.add(a.id);
  }
  for (const r of removed) visible.delete(r);

  const pendingEffects = triggers
    .filter((t) => triggeredAt.has(t.rule.id) && !applied.has(t.rule.id))
    .map((t) => ({
      id: t.rule.id,
      readyAtTick: (triggeredAt.get(t.rule.id) ?? 0) + t.rule.effect.afterTicks,
    }));

  return {
    viewedArtifacts: viewed,
    pinnedEvidence: pinned,
    notes,
    boardLinks: links,
    searches,
    accusation: {
      attemptsLeft: MAX_ACCUSATION_ATTEMPTS - attempts.length,
      attempts,
      solved,
      failed: !solved && attempts.length >= MAX_ACCUSATION_ATTEMPTS,
    },
    clockTicks: s.ticks,
    visibleArtifacts: visible,
    removedArtifacts: removed,
    notifications,
    pendingEffects,
    milestones,
    hintsUsed,
    quotes,
    requests,
    appliedRules: applied,
  };
}

/**
 * Estado diegético de una solicitud (F1-T08): "respondido" si alguna regla que
 * la menciona ya aplicó su efecto; "en trámite" si existe la regla y no maduró;
 * "sin resultados" si el caso no tiene nada que responder a ese formulario.
 */
export function requestStatus(
  kase: CaseV1Data,
  derived: DerivedState,
  formId: string,
): "respondido" | "en tramite" | "sin resultados" {
  const mentions = (cond: ConditionV1Data): boolean => {
    if ("requested" in cond) return cond.requested === formId;
    if ("allOf" in cond) return cond.allOf.some(mentions);
    if ("anyOf" in cond) return cond.anyOf.some(mentions);
    return false;
  };
  const rules = [...kase.unlocks, ...kase.scriptedEvents].filter((r) => mentions(r.when));
  if (rules.length === 0) return "sin resultados";
  if (rules.some((r) => derived.appliedRules.has(r.id))) return "respondido";
  return "en tramite";
}

/** Artefactos que una solicitud respondida dejó disponibles (para "ver en el expediente") */
export function requestDeliverables(kase: CaseV1Data, formId: string): string[] {
  const mentions = (cond: ConditionV1Data): boolean => {
    if ("requested" in cond) return cond.requested === formId;
    if ("allOf" in cond) return cond.allOf.some(mentions);
    if ("anyOf" in cond) return cond.anyOf.some(mentions);
    return false;
  };
  return kase.unlocks.filter((u) => mentions(u.when)).flatMap((u) => u.effect.unlockArtifacts);
}
